import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ChevronDownIcon,
  ChevronUpIcon,
  CameraIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import LoadingSpinner from '@/components/LoadingSpinner'
import { useLprEvents } from '@/hooks/useLprEvents'
import { useVehicles } from '@/hooks/useVehicles'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import type { LprEvent } from '@/types'

const REFETCH_INTERVAL_MS = 30_000
const PER_PAGE = 20

// ─── Helpers ─────────────────────────────────────────────────────────────────

const VITE_API_URL = import.meta.env.VITE_API_URL ?? ''

function thumbUrl(key: string): string {
  return `${VITE_API_URL}/api/images/${key}`
}

// ─── Lightbox ─────────────────────────────────────────────────────────────────

interface LightboxProps {
  event: LprEvent
  onClose: () => void
}

function Lightbox({ event, onClose }: LightboxProps) {
  const { t } = useTranslation('lpr_events')

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 bg-black/80 flex flex-col pointer-events-auto"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={t('lightbox.title')}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 text-white"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="font-semibold text-base">{t('lightbox.title')}</span>
        <button
          onClick={onClose}
          aria-label={t('lightbox.close')}
          className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Images */}
      <div
        className="flex-1 overflow-y-auto px-4 pb-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Front */}
        <div>
          <p className="text-xs text-white/60 uppercase tracking-wide mb-2">
            {t('lightbox.front')}
          </p>
          {event.front_thumb_key ? (
            <img
              src={thumbUrl(event.front_thumb_key)}
              alt={t('lightbox.front')}
              className="w-full rounded-lg object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-32 rounded-lg bg-white/10 flex items-center justify-center text-white/40 text-sm">
              {t('lightbox.no_image')}
            </div>
          )}
        </div>

        {/* Rear */}
        <div>
          <p className="text-xs text-white/60 uppercase tracking-wide mb-2">
            {t('lightbox.rear')}
          </p>
          {event.rear_thumb_key ? (
            <img
              src={thumbUrl(event.rear_thumb_key)}
              alt={t('lightbox.rear')}
              className="w-full rounded-lg object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-32 rounded-lg bg-white/10 flex items-center justify-center text-white/40 text-sm">
              {t('lightbox.no_image')}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Event Card ───────────────────────────────────────────────────────────────

interface EventCardProps {
  event: LprEvent
  onViewCapture: (event: LprEvent) => void
}

function EventCard({ event, onViewCapture }: EventCardProps) {
  const { t } = useTranslation('lpr_events')
  const { formatDate } = useDateFormatter()

  const directionLabel =
    event.direction === 'in'
      ? t('direction.in')
      : event.direction === 'out'
        ? t('direction.out')
        : t('direction.unknown')

  const directionBadge =
    event.direction === 'in'
      ? 'status-badge-success'
      : event.direction === 'out'
        ? 'status-badge-ghost'
        : 'status-badge-ghost'

  // Status enum is the Rails LprEvent contract: pending | verified_success |
  // verified_failed (api/app/models/lpr_event.rb). Anything else → pending.
  const statusBadgeClass =
    event.verification_status === 'verified_success'
      ? 'status-badge-success'
      : event.verification_status === 'verified_failed'
        ? 'status-badge-error'
        : 'status-badge-warning'

  const statusLabel =
    event.verification_status === 'verified_success'
      ? t('status.verified_success')
      : event.verification_status === 'verified_failed'
        ? t('status.verified_failed')
        : t('status.pending')

  return (
    <div className="flat-card p-4 space-y-2">
      {/* Top row: date + direction + status badge */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-base-content/60 flex-1 min-w-0">
          {formatDate(event.captured_at)}
        </span>
        <span className={directionBadge}>{directionLabel}</span>
        <span className={statusBadgeClass}>{statusLabel}</span>
      </div>

      {/* Details grid */}
      <div className="space-y-1 text-sm">
        <div className="flex justify-between gap-2">
          <span className="text-base-content/60">{t('card.plate')}</span>
          <span className="font-mono font-medium">
            {event.plate_text ?? t('card.no_plate')}
          </span>
        </div>

        <div className="flex justify-between gap-2">
          <span className="text-base-content/60">{t('card.vehicle_id')}</span>
          <span className="font-medium">#{event.vehicle_id}</span>
        </div>

        {event.ml_re_id_score !== null && event.ml_re_id_score !== undefined && (
          <div className="flex justify-between gap-2">
            <span className="text-base-content/60">{t('card.score')}</span>
            <span className="font-medium">{(event.ml_re_id_score * 100).toFixed(1)}%</span>
          </div>
        )}
      </div>

      {/* View capture link */}
      <button
        type="button"
        onClick={() => onViewCapture(event)}
        className="text-xs text-primary font-semibold mt-1"
        aria-label={t('card.view_capture')}
      >
        {t('card.view_capture')}
      </button>
    </div>
  )
}

// ─── Filter block ─────────────────────────────────────────────────────────────

interface FilterValues {
  vehicleId: number | null
  direction: 'in' | 'out' | null
  capturedAtFrom: string
  capturedAtTo: string
}

interface FilterBlockProps {
  filters: FilterValues
  onChange: (filters: FilterValues) => void
  vehicles: Array<{ id: number; license_plate: string }>
}

function FilterBlock({ filters, onChange, vehicles }: FilterBlockProps) {
  const { t } = useTranslation('lpr_events')
  const [open, setOpen] = useState(false)

  const activeCount =
    (filters.vehicleId !== null ? 1 : 0) +
    (filters.direction !== null ? 1 : 0) +
    (filters.capturedAtFrom ? 1 : 0) +
    (filters.capturedAtTo ? 1 : 0)

  const handleClear = () => {
    onChange({ vehicleId: null, direction: null, capturedAtFrom: '', capturedAtTo: '' })
  }

  return (
    <div className="flat-card">
      {/* Toggle row */}
      <button
        type="button"
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-base-content"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-controls="lpr-filter-body"
      >
        <span className="flex items-center gap-2">
          {t('filter.label')}
          {activeCount > 0 && (
            <span className="status-badge-info">{t('filter.active_count', { count: activeCount })}</span>
          )}
        </span>
        {open ? (
          <ChevronUpIcon className="w-4 h-4 text-base-content/50" aria-hidden="true" />
        ) : (
          <ChevronDownIcon className="w-4 h-4 text-base-content/50" aria-hidden="true" />
        )}
      </button>

      {/* Filter body */}
      <div id="lpr-filter-body" className={open ? 'px-4 pb-4 space-y-3' : 'hidden'}>
        {/* Vehicle selector */}
        <div className="space-y-1">
          <label className="text-xs text-base-content/60 font-medium">{t('filter.vehicle')}</label>
          <select
            className="select select-bordered select-sm w-full"
            value={filters.vehicleId ?? ''}
            onChange={(e) =>
              onChange({
                ...filters,
                vehicleId: e.target.value === '' ? null : Number(e.target.value),
              })
            }
            aria-label={t('filter.vehicle')}
          >
            <option value="">{t('filter.all_vehicles')}</option>
            {vehicles.map((v) => (
              <option key={v.id} value={v.id}>
                {v.license_plate}
              </option>
            ))}
          </select>
        </div>

        {/* Direction selector */}
        <div className="space-y-1">
          <label className="text-xs text-base-content/60 font-medium">{t('filter.direction')}</label>
          <div className="flex gap-2">
            {(['', 'in', 'out'] as const).map((d) => {
              const label =
                d === '' ? t('filter.direction_all') : d === 'in' ? t('filter.direction_in') : t('filter.direction_out')
              const isActive = (filters.direction ?? '') === d
              return (
                <button
                  key={d}
                  type="button"
                  className={`flex-1 py-1.5 text-xs font-medium rounded-full border transition-all ${
                    isActive
                      ? 'bg-primary text-primary-content border-primary'
                      : 'bg-base-100 text-base-content border-base-300'
                  }`}
                  onClick={() =>
                    onChange({ ...filters, direction: d === '' ? null : d })
                  }
                  aria-pressed={isActive}
                >
                  {label}
                </button>
              )
            })}
          </div>
        </div>

        {/* Date range */}
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <label className="text-xs text-base-content/60 font-medium">{t('filter.date_from')}</label>
            <input
              type="date"
              className="input input-bordered input-sm w-full"
              value={filters.capturedAtFrom}
              onChange={(e) => onChange({ ...filters, capturedAtFrom: e.target.value })}
              aria-label={t('filter.date_from')}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-base-content/60 font-medium">{t('filter.date_to')}</label>
            <input
              type="date"
              className="input input-bordered input-sm w-full"
              value={filters.capturedAtTo}
              onChange={(e) => onChange({ ...filters, capturedAtTo: e.target.value })}
              aria-label={t('filter.date_to')}
            />
          </div>
        </div>

        {/* Clear */}
        {activeCount > 0 && (
          <button
            type="button"
            className="btn btn-ghost btn-sm w-full"
            onClick={handleClear}
          >
            {t('filter.clear')}
          </button>
        )}
      </div>
    </div>
  )
}

// ─── Pagination ───────────────────────────────────────────────────────────────

interface PaginationProps {
  page: number
  totalPages: number
  onPageChange: (page: number) => void
}

function Pagination({ page, totalPages, onPageChange }: PaginationProps) {
  const { t } = useTranslation('lpr_events')

  if (totalPages <= 1) return null

  return (
    <div className="flex gap-2 justify-center pt-2">
      <button
        className="btn btn-ghost btn-sm"
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
        aria-label="Previous page"
      >
        ‹
      </button>
      <span className="btn btn-ghost btn-sm no-animation cursor-default">
        {t('page_of', { page, total: totalPages })}
      </span>
      <button
        className="btn btn-ghost btn-sm"
        disabled={page >= totalPages}
        onClick={() => onPageChange(page + 1)}
        aria-label="Next page"
      >
        ›
      </button>
    </div>
  )
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function LprEventsPage() {
  const { t } = useTranslation('lpr_events')
  const { t: tc } = useTranslation('common')

  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState<FilterValues>({
    vehicleId: null,
    direction: null,
    capturedAtFrom: '',
    capturedAtTo: '',
  })
  const [lightboxEvent, setLightboxEvent] = useState<LprEvent | null>(null)

  const { data: vehiclesData } = useVehicles()
  const vehicles = vehiclesData ?? []

  const { data, isLoading, error, isFetching } = useLprEvents({
    perPage: PER_PAGE,
    page,
    vehicleId: filters.vehicleId,
    direction: filters.direction,
    capturedAtFrom: filters.capturedAtFrom || null,
    capturedAtTo: filters.capturedAtTo || null,
    refetchInterval: REFETCH_INTERVAL_MS,
  })

  const events = data?.data ?? []
  const meta = data?.meta
  const totalPages = meta?.pages ?? 1

  const handleFilterChange = (next: FilterValues) => {
    setFilters(next)
    setPage(1)
  }

  return (
    <div className="p-4 space-y-3">
      {/* Filter block */}
      <FilterBlock
        filters={filters}
        onChange={handleFilterChange}
        vehicles={vehicles}
      />

      {/* Refreshing indicator */}
      {isFetching && !isLoading && (
        <div className="flex justify-center">
          <span className="loading loading-spinner loading-xs text-primary" aria-label={tc('status.loading')} />
        </div>
      )}

      {/* Loading */}
      {isLoading && <LoadingSpinner />}

      {/* Error */}
      {error && (
        <div className="alert alert-error">{tc('errors.something_went_wrong')}</div>
      )}

      {/* Empty state */}
      {!isLoading && !error && events.length === 0 && (
        <div className="text-center py-12 text-base-content/50">
          <div className="flex justify-center mb-3">
            <div className="icon-container">
              <CameraIcon className="w-5 h-5" />
            </div>
          </div>
          <p className="font-medium">{t('no_events')}</p>
          <p className="text-sm mt-1">{t('no_events_sub')}</p>
        </div>
      )}

      {/* Event cards */}
      {events.map((event) => (
        <EventCard
          key={event.id}
          event={event}
          onViewCapture={setLightboxEvent}
        />
      ))}

      {/* Pagination */}
      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />

      {/* Lightbox */}
      {lightboxEvent && (
        <Lightbox event={lightboxEvent} onClose={() => setLightboxEvent(null)} />
      )}
    </div>
  )
}
